import cryptoJs from "crypto-js";
import {app} from "./config.ts";
import {format, parse} from "date-fns";
import _ from "lodash";

export class Utils {
  static toObject<T>(ClassType: new (data: object) => T, json: object): T {
    return new ClassType(json);
  }

  static uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random()*16|0, v = c === 'x' ? r : (r&0x3|0x8);
      return v.toString(16);
    });
  }

  /**
   * WKWebView(iOS 네이티브 앱)의 메시지 브릿지 존재 여부.
   * App Store에서 받은 iOS 앱을 Apple Silicon Mac에서 실행하면 UA가 Mac으로
   * 보고되므로, UA 대신 브릿지 유무로 네이티브 앱을 판별한다.
   */
  static isIosNativeApp(): boolean {
    try {
      return typeof window.webkit?.messageHandlers?.Interface?.postMessage === 'function';
    } catch {
      return false;
    }
  }

  /**
   * 커스텀 UA(MacintoshApp/1.0.0, WindowsApp/1.0.0 등)에서 앱 이름을 추출한다.
   * 표준 브라우저 UA는 모두 "Mozilla/5.0..." 으로 시작하므로 앱 이름으로 보지 않는다.
   */
  private static appNameFromUserAgent(userAgent: string): string | null {
    const parsed = userAgent.match(/^([^\/\s]+)\/(\S+)/);
    const name = parsed?.[1];
    return name && name !== 'Mozilla' ? name : null;
  }

  static osname() {
    const userAgent: any = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'iOS';
    } else if (/MacIntel|Macintosh|Mac OS/.test(userAgent)) {
      // App Store에서 받은 iOS 앱을 Apple Silicon Mac에서 실행하는 경우
      // UA는 Mac으로 보고되지만 실제로는 iOS 네이티브 앱이다.
      if (this.isIosNativeApp()) {
        return 'iOS';
      }
      if (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) {
        return 'iOS'; // iPad
      }
      // MacintoshApp/x.x.x 형태
      return this.appNameFromUserAgent(userAgent) ?? 'macOS';
    } else if (/android/i.test(userAgent)) {
      return 'Android';
    } else if (/windows/i.test(userAgent)) {
      if (/Windows NT 10.0/.test(userAgent)) return 'Windows 10';
      if (/Windows NT 6.3/.test(userAgent)) return 'Windows 8.1';
      if (/Windows NT 6.2/.test(userAgent)) return 'Windows 8';
      if (/Windows NT 6.1/.test(userAgent)) return 'Windows 7';
      if (/Windows NT 6.0/.test(userAgent)) return 'Windows Vista';
      if (/Windows NT 5.1/.test(userAgent)) return 'Windows XP';
      if (/Windows NT 5.0/.test(userAgent)) return 'Windows 2000';

      // WindowsApp/x.x.x 형태
      return this.appNameFromUserAgent(userAgent) ?? 'Windows';
    } else if (/linux/i.test(userAgent)) {
      return 'Linux';
    } else {
      return 'Unknown';
    }
}

  static isMobileOrTableDevice() {
    const userAgent: string = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileOrTabletUA = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|windowsapp/i.test(userAgent);

    const maxWidth = 1024;
    const isSmallScreen = window.innerWidth <= maxWidth || window.outerWidth <= maxWidth;
    const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

    const isIPadOSDesktopMode = /macintosh|mac os x/i.test(userAgent) && hasTouch && isSmallScreen;

    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    const isNoHover = window.matchMedia('(hover: none)').matches;
    const isAnyHoverNone = window.matchMedia('(any-hover: none)').matches;

    return isMobileOrTabletUA || isIPadOSDesktopMode || (isSmallScreen && hasTouch) || isCoarsePointer || isNoHover || isAnyHoverNone;
  }

  static iOSDevice(): boolean {
    return _.includes(['iOS'], this.osname()) && this.isMobileOrTableDevice();
  }

  static pad(data: number = 0, len = 2, chars = '0'): string {
    return data.toString().padStart(len, chars);
  }

  static now(milliseconds: number = 0): Date {
    return milliseconds === 0 ? new Date() : new Date(milliseconds);
  }

  static milliseconds(date: Date | null = null): number {
    if(_.isNull(date)) date = this.now();
    return date?.getTime();
  }

  static duration(seconds: number, korean: boolean = true): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    const mm = Utils.pad(m);
    const ss = Utils.pad(s);
    return korean ? `${mm}분 ${ss}초` : `${mm}:${ss}`;
  }

  static toDate(str: string, date_formmat: string = 'yyyy-MM-dd HH:mm:ss', date: Date | undefined = undefined): Date {
    if(!date) date = this.now();
    return parse(str, date_formmat, date);
  }

  static toDateString(date: Date | undefined = undefined, date_formmat: string = 'yyyy-MM-dd HH:mm:ss'): string {
    if(!date) date = this.now();
    return format(date, date_formmat);
  }

  static saveConfig(key: string, data: any) {
    try {
      const json = JSON.stringify(data);
      const CHUNK = 64 * 1024;

      window.wpi?.save_begin(key, json.length);

      for (let i = 0; i < json.length; i += CHUNK) {
        const part = json.slice(i, i + CHUNK);
        window.wpi?.save_chunk(key, i / CHUNK, part);
      }

      window.wpi?.save_end(key);
    } catch (e) {}
  }

  static base64encode(str: string): string {
    const arrays = cryptoJs.enc.Utf8.parse(str);
    return cryptoJs.enc.Base64.stringify(arrays);
  }

  static base64decode(str: string): string {
    const arrays = cryptoJs.enc.Base64.parse(str);
    return arrays.toString(cryptoJs.enc.Utf8);
  }

  static sha256(str: string): string {
    return cryptoJs.SHA256(str).toString(cryptoJs.enc.Hex);
  }

  static encrypt(str: string, key: string = app.hash, iv: string = '0000000000000000'): string | null {
    try {
      const _key = cryptoJs.enc.Utf8.parse(key);
      const _iv = cryptoJs.enc.Utf8.parse(iv);

      return cryptoJs.AES.encrypt(
        str,
        _key,
        {
          iv: _iv,
          mode: cryptoJs.mode.CBC,
          padding: cryptoJs.pad.Pkcs7
        }
      ).toString();
    } catch(e) {
      void e;
    }
    return null;
  }

  static decrypt(str: string, key: string = app.hash, iv: string = '0000000000000000'): string | null {
    try {
      const _key = cryptoJs.enc.Utf8.parse(key);
      const _iv = cryptoJs.enc.Utf8.parse(iv);

      const decrypted = cryptoJs.AES.decrypt(
        str,
        _key,
        {
          iv: _iv,
          mode: cryptoJs.mode.CBC,
          padding: cryptoJs.pad.Pkcs7
        }
      );
      return decrypted.toString(cryptoJs.enc.Utf8);
    } catch (e) {
      console.error(e);
    }
    return null;
  }

  static toJson(data: object, crypt: boolean = false): string | null {
    try {
      let json: string | null = JSON.stringify(data);
      if(crypt) json = this.encrypt(json);
      return json;
    } catch (e) {
      console.error(e);
    }
    return null;
  }

  static fromJson<T>(data: string, ClassType?: new (data: object) => T, crypt: boolean = false): T | null {
    if(crypt) data = this.decrypt(data) ?? '{}';
    try {
      const json = JSON.parse(data);
      if(ClassType != null) {
        return this.toObject(ClassType, json);
      }
      return json as T;
    } catch (e) {
      console.error(e);
    }
    return null;
  }

  static strLength(str: string = ''): number {
    let byteLength = 0;
    for (let i = 0; i < str.length; i++) {
      const charCode = str.charCodeAt(i);
      // 한글(AC00-D7A3) 범위: 유니코드 한글 음절
      if (charCode >= 0xAC00 && charCode <= 0xD7A3) {
        byteLength += 2;
      } else {
        byteLength += 1;
      }
    }
    return byteLength;
  };

  /**
   * 주어진 문자열을 최대 바이트 길이에 맞춰 자릅니다.
   * 한글은 2바이트, 그 외 문자는 1바이트로 계산합니다.
   * @param {string} str - 자를 문자열
   * @param {number} maxLengthBytes - 최대 허용 바이트 길이
   * @returns {string} - 잘린 문자열
   */
  static strCut(str: string, maxLengthBytes: number): string {
    if (!str) return '';

    let currentByteLength = 0;
    let truncatedString = '';

    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      const charCode = str.charCodeAt(i);
      const charByteLength = (charCode >= 0xAC00 && charCode <= 0xD7A3) ? 2 : 1;

      if (currentByteLength + charByteLength <= maxLengthBytes) {
        truncatedString += char;
        currentByteLength += charByteLength;
      } else {
        break;
      }
    }
    return truncatedString;
  };

  static isLocalMedia(url?: string): boolean {
    return url ? url.includes("/medias/") : false;
  }
}